// @ts-check

/** @file Upload-session helpers for files embedded in HTML editor fields. */

import {UploadedFile} from "../../../frontend-models/uploaded-file"

export const editorAttachmentAttribute = "data-editor-attachment"
export const pendingUploadAttribute = "data-pending-upload-id"
export const uploadedFileAttribute = "data-uploaded-file-id"
const uploadedFileSourcePrefix = "uploaded-file:"

/** @type {Map<string, string>} */
const resolvedUploadedFileSources = new Map()

/**
 * @typedef {object} EditorUploadPayload
 * @property {string} contentBase64 Base64 encoded file bytes.
 * @property {string} contentType File MIME type.
 * @property {string} filename File filename.
 * @property {number | null | undefined} [sizeBytes] Browser-reported file size.
 */

/**
 * @typedef {object} FlushHtmlArgs
 * @property {string} html Current editor HTML.
 * @property {string | number} resourceId Owning resource id.
 * @property {string} resourceType Owning resource model name.
 */

/**
 * @typedef {object} FlushNestedHtmlArgs
 * @property {string} html Current editor HTML.
 * @property {UploadedFile[]} uploadedFiles Uploaded files returned by the nested parent save.
 */

/** Tracks unsaved editor files until the parent record can own them. */
export class FormHtmlEditorUploadSession {
  /** @type {Map<string, EditorUploadPayload>} */
  pendingUploads = new Map()

  /** Clears pending uploads that were not saved. */
  reset() {
    this.pendingUploads.clear()
  }

  /**
   * Registers a file selected in the editor before the owning record is saved.
   * @param {EditorUploadPayload} payload Pending file upload.
   * @returns {string} Pending upload id to embed in the editor HTML.
   */
  registerUpload(payload) {
    const pendingUploadId = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`

    this.pendingUploads.set(pendingUploadId, payload)

    return pendingUploadId
  }

  /**
   * Builds nested UploadedFile attributes for pending files still referenced by the HTML.
   * @param {string} html Current editor HTML.
   * @param {string | null} userId Current user id for uploaded-file ownership.
   * @returns {{file: EditorUploadPayload, title: string, userId: string}[]} Nested UploadedFile create attributes.
   * @throws {Error} When a referenced pending upload is missing.
   */
  nestedUploadedFilesAttributes(html, userId) {
    return uploadAttributeValues(String(html || ""), pendingUploadAttribute).map((pendingUploadId) => {
      const pendingUpload = this.pendingUploads.get(pendingUploadId)

      if (!pendingUpload) {
        throw new Error(`Missing pending editor file upload ${pendingUploadId}`)
      }

      if (!userId) {
        throw new Error("Current user id is required for editor file uploads")
      }

      return {
        file: pendingUpload,
        title: pendingUpload.filename,
        userId
      }
    })
  }

  /**
   * Rewrites pending upload tags after a parent nested save created UploadedFile records.
   * @param {FlushNestedHtmlArgs} args HTML and nested UploadedFile records.
   * @returns {string} HTML safe to persist.
   * @throws {Error} When a pending upload cannot be mapped to a saved UploadedFile.
   */
  flushNestedUploadedFilesHtml({html, uploadedFiles}) {
    let nextHtml = normalizeUploadedFileImageSources(String(html || ""))
    const pendingUploadIds = uploadAttributeValues(nextHtml, pendingUploadAttribute)
    const uploadedFilesByPendingUploadId = this.uploadedFilesByPendingUploadId({pendingUploadIds, uploadedFiles})

    for (const pendingUploadId of pendingUploadIds) {
      const pendingUpload = this.pendingUploads.get(pendingUploadId)
      const uploadedFile = uploadedFilesByPendingUploadId.get(pendingUploadId)

      if (!pendingUpload) {
        throw new Error(`Missing pending editor file upload ${pendingUploadId}`)
      }

      if (!uploadedFile) {
        throw new Error(`Could not match pending editor file upload ${pendingUpload.filename} to a saved uploaded file`)
      }

      nextHtml = replaceUploadTags(nextHtml, (tag) => {
        if (readAttribute(tag, pendingUploadAttribute) !== pendingUploadId) return tag

        return normalizeFlushedUploadTag(
          tag,
          pendingUpload,
          String(uploadedFile.id())
        )
      })
      this.pendingUploads.delete(pendingUploadId)
    }

    return normalizeUploadedFileImageSources(nextHtml)
  }

  /**
   * Maps pending upload ids to the newest matching uploaded files returned by a nested save.
   * @param {{pendingUploadIds: string[], uploadedFiles: UploadedFile[]}} args Pending ids and saved files.
   * @returns {Map<string, UploadedFile>} Saved uploaded files keyed by pending upload id.
   */
  uploadedFilesByPendingUploadId({pendingUploadIds, uploadedFiles}) {
    /** @type {Map<string, number>} */
    const pendingCountByFilename = new Map()
    /** @type {Map<string, UploadedFile[]>} */
    const candidatesByFilename = new Map()
    /** @type {Map<string, UploadedFile[]>} */
    const selectedByFilename = new Map()
    /** @type {Map<string, UploadedFile>} */
    const uploadedFilesByPendingUploadId = new Map()

    for (const pendingUploadId of pendingUploadIds) {
      const pendingUpload = this.pendingUploads.get(pendingUploadId)

      if (!pendingUpload) continue

      pendingCountByFilename.set(pendingUpload.filename, (pendingCountByFilename.get(pendingUpload.filename) || 0) + 1)
    }

    for (const uploadedFile of uploadedFiles) {
      const title = uploadedFile.title()
      const fileName = uploadedFile.fileName()

      for (const filename of pendingCountByFilename.keys()) {
        if (title !== filename && fileName !== filename) continue

        let candidates = candidatesByFilename.get(filename)

        if (!candidates) {
          candidates = []
          candidatesByFilename.set(filename, candidates)
        }

        candidates.push(uploadedFile)
      }
    }

    for (const [filename, count] of pendingCountByFilename.entries()) {
      const candidates = (candidatesByFilename.get(filename) || [])
        .sort((left, right) => {
          const createdAtDifference = sortableTimestamp(left.createdAt()) - sortableTimestamp(right.createdAt())

          return createdAtDifference || String(left.id() || "").localeCompare(String(right.id() || ""))
        })

      selectedByFilename.set(filename, candidates.slice(-count))
    }

    for (const pendingUploadId of pendingUploadIds) {
      const pendingUpload = this.pendingUploads.get(pendingUploadId)

      if (!pendingUpload) continue

      const selected = selectedByFilename.get(pendingUpload.filename) || []
      const uploadedFile = selected.shift()

      if (uploadedFile) uploadedFilesByPendingUploadId.set(pendingUploadId, uploadedFile)
    }

    return uploadedFilesByPendingUploadId
  }

  /**
   * Uploads pending editor files and normalizes saved references.
   * @param {FlushHtmlArgs} args HTML and owner metadata.
   * @returns {Promise<string>} HTML safe to persist.
   * @rejects {Error} When a pending upload cannot be found or persisted.
   */
  async flushHtml({html, resourceId, resourceType}) {
    let nextHtml = normalizeUploadedFileImageSources(String(html || ""))
    const pendingUploadIds = uploadAttributeValues(nextHtml, pendingUploadAttribute)

    for (const pendingUploadId of pendingUploadIds) {
      const pendingUpload = this.pendingUploads.get(pendingUploadId)

      if (!pendingUpload) {
        throw new Error(`Missing pending editor file upload ${pendingUploadId}`)
      }

      let response

      try {
        response = await UploadedFile.createWithFile({
          resourceId,
          resourceType,
          title: pendingUpload.filename,
          uploadedFile: pendingUpload
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        const uploadError = /** @type {Error & {velocious?: {code: string}}} */ (new Error(`Could not upload ${pendingUpload.filename}: ${message}`))

        uploadError.velocious = {code: "form-html-editor-upload-failed"}
        throw uploadError
      }
      const uploadedFileId = response.id

      if (!uploadedFileId) {
        throw new Error("Expected uploaded-file command to return an uploaded-file id")
      }

      nextHtml = replaceUploadTags(nextHtml, (tag) => {
        if (readAttribute(tag, pendingUploadAttribute) !== pendingUploadId) return tag

        return normalizeFlushedUploadTag(
          tag,
          pendingUpload,
          String(uploadedFileId)
        )
      })
      this.pendingUploads.delete(pendingUploadId)
    }

    return normalizeUploadedFileImageSources(nextHtml)
  }
}

/**
 * Converts a model timestamp into a stable sortable number.
 * @param {Date | string | null} value Uploaded-file creation time to compare.
 * @returns {number} Milliseconds since epoch, or zero when absent/invalid.
 */
function sortableTimestamp(value) {
  if (!value) return 0
  const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime()

  return Number.isFinite(timestamp) ? timestamp : 0
}

/**
 * Resolves persisted uploaded-file image references to renderable data URLs.
 * @param {string | null | undefined} html Persisted HTML.
 * @returns {Promise<string>} HTML with uploaded image sources resolved.
 */
export async function resolveEditorUploadedFileImageSources(html) {
  let nextHtml = String(html || "")
  const uploadedFileIds = imageAttributeValues(nextHtml, uploadedFileAttribute)

  for (const uploadedFileId of uploadedFileIds) {
    let imageSource

    try {
      imageSource = await uploadedFileImageSource(uploadedFileId)
    } catch (error) {
      reportUploadedFileImageSourceFailure(uploadedFileId, error)
      nextHtml = replaceImageTags(nextHtml, (tag) => {
        if (readAttribute(tag, uploadedFileAttribute) !== uploadedFileId) return tag

        return removeAttribute(tag, "src")
      })
      continue
    }

    nextHtml = replaceImageTags(nextHtml, (tag) => {
      if (readAttribute(tag, uploadedFileAttribute) !== uploadedFileId) return tag

      return setAttribute(tag, "src", imageSource)
    })
  }

  return nextHtml
}

/**
 * Clears a cached image data URL after the uploaded file bytes change.
 * @param {string | number} uploadedFileId Uploaded-file id to invalidate.
 * @returns {void} Nothing.
 */
export function invalidateUploadedFileImageSource(uploadedFileId) {
  resolvedUploadedFileSources.delete(String(uploadedFileId))
}

/**
 * Fetches one uploaded-file image source.
 * @param {string} uploadedFileId Record id to download through the frontend model command.
 * @returns {Promise<string>} Data URL for the image.
 * @rejects {Error} When the uploaded image has no downloadable content.
 */
async function uploadedFileImageSource(uploadedFileId) {
  const cachedSource = resolvedUploadedFileSources.get(uploadedFileId)

  if (cachedSource) return cachedSource

  const uploadedFile = await UploadedFile.find(uploadedFileId)
  const payload = await uploadedFile.downloadFile()
  const contentBase64 = typeof payload.contentBase64 === "string" ? payload.contentBase64 : ""
  const contentType = typeof payload.contentType === "string" && payload.contentType ? payload.contentType : "application/octet-stream"

  if (!contentBase64) {
    throw new Error(`Uploaded image ${uploadedFileId} returned no content`)
  }

  const imageSource = `data:${contentType};base64,${contentBase64}`

  resolvedUploadedFileSources.set(uploadedFileId, imageSource)

  return imageSource
}

/**
 * Logs a recoverable uploaded-file image lookup failure.
 * @param {string} uploadedFileId Uploaded-file id from the rendered image tag.
 * @param {unknown} error Error raised while loading image bytes.
 * @returns {void} Nothing.
 */
function reportUploadedFileImageSourceFailure(uploadedFileId, error) {
  console.warn(`Could not resolve uploaded image ${uploadedFileId}`, error)
}

/**
 * Adds uploaded-file metadata to one flushed editor upload tag.
 * @param {string} tag Opening HTML tag.
 * @param {EditorUploadPayload} pendingUpload Pending upload metadata.
 * @param {string} uploadedFileId Persisted uploaded-file id.
 * @returns {string} Rewritten opening tag.
 */
function normalizeFlushedUploadTag(tag, pendingUpload, uploadedFileId) {
  let nextTag = setAttribute(
    removeAttribute(tag, pendingUploadAttribute),
    uploadedFileAttribute,
    uploadedFileId
  )

  if (isImageTag(tag)) {
    return setAttribute(nextTag, "src", `${uploadedFileSourcePrefix}${uploadedFileId}`)
  }

  nextTag = setAttribute(nextTag, editorAttachmentAttribute, "true")
  nextTag = setAttribute(nextTag, "data-file-name", pendingUpload.filename)
  nextTag = setAttribute(nextTag, "data-content-type", pendingUpload.contentType)

  if (typeof pendingUpload.sizeBytes === "number") {
    nextTag = setAttribute(nextTag, "data-file-size", String(pendingUpload.sizeBytes))
  }

  return nextTag
}

/**
 * Rewrites persisted image src attributes to stable uploaded-file placeholders.
 * @param {string} html HTML to normalize.
 * @returns {string} Normalized HTML.
 */
function normalizeUploadedFileImageSources(html) {
  return replaceImageTags(html, (tag) => {
    const uploadedFileId = readAttribute(tag, uploadedFileAttribute)

    if (!uploadedFileId) return tag

    return setAttribute(tag, "src", `${uploadedFileSourcePrefix}${uploadedFileId}`)
  })
}

/**
 * Extracts image attribute values.
 * @param {string} html HTML to inspect.
 * @param {string} attributeName Image attribute to read.
 * @returns {string[]} Unique attribute values.
 */
function imageAttributeValues(html, attributeName) {
  /** @type {Set<string>} */
  const values = new Set()

  replaceImageTags(html, (tag) => {
    const value = readAttribute(tag, attributeName)

    if (value) values.add(value)

    return tag
  })

  return Array.from(values)
}

/**
 * Extracts pending/uploaded ids from editor upload tags.
 * @param {string} html HTML to inspect.
 * @param {string} attributeName Attribute to read.
 * @returns {string[]} Unique attribute values.
 */
function uploadAttributeValues(html, attributeName) {
  /** @type {Set<string>} */
  const values = new Set()

  replaceUploadTags(html, (tag) => {
    const value = readAttribute(tag, attributeName)

    if (value) values.add(value)

    return tag
  })

  return Array.from(values)
}

/**
 * Replaces every image tag in an HTML fragment.
 * @param {string} html HTML fragment.
 * @param {(tag: string) => string} callback Replacement callback.
 * @returns {string} Rewritten HTML.
 */
function replaceImageTags(html, callback) {
  return html.replace(/<img\b[^>]*>/gi, (tag) => callback(tag))
}

/**
 * Replaces image and attachment opening tags in an HTML fragment.
 * @param {string} html HTML fragment.
 * @param {(tag: string) => string} callback Replacement callback.
 * @returns {string} Rewritten HTML.
 */
function replaceUploadTags(html, callback) {
  return html.replace(/<(?:img|div)\b[^>]*>/gi, (tag) => {
    if (!isImageTag(tag) && readAttribute(tag, editorAttachmentAttribute) !== "true") return tag

    return callback(tag)
  })
}

/**
 * Checks whether an opening tag is an image.
 * @param {string} tag Opening tag.
 * @returns {boolean} True when the tag starts an image element.
 */
function isImageTag(tag) {
  return /^<img\b/i.test(tag)
}

/**
 * Reads an HTML attribute from one tag.
 * @param {string} tag HTML tag string.
 * @param {string} attributeName Attribute to read.
 * @returns {string | null} Attribute value when present.
 */
function readAttribute(tag, attributeName) {
  const match = tag.match(new RegExp(`\\s${escapeRegExp(attributeName)}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, "i"))

  if (!match) return null

  return unescapeAttribute(match[1] || match[2] || match[3] || "")
}

/**
 * Removes an HTML attribute from one tag.
 * @param {string} tag HTML tag string.
 * @param {string} attributeName Attribute to remove.
 * @returns {string} Tag without the attribute.
 */
function removeAttribute(tag, attributeName) {
  return tag.replace(new RegExp(`\\s${escapeRegExp(attributeName)}\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s>]+)`, "gi"), "")
}

/**
 * Sets an HTML attribute on one tag.
 * @param {string} tag HTML tag string.
 * @param {string} attributeName Attribute to set.
 * @param {string} value New raw value to escape into the tag.
 * @returns {string} Tag with the updated attribute.
 */
function setAttribute(tag, attributeName, value) {
  const normalizedTag = removeAttribute(tag, attributeName)
  const closingMatch = normalizedTag.match(/\s*\/?>$/)
  const closing = closingMatch ? closingMatch[0] : ">"
  const prefix = normalizedTag.slice(0, normalizedTag.length - closing.length).trimEnd()
  const suffix = closing.includes("/") ? " />" : ">"

  return `${prefix} ${attributeName}="${escapeAttribute(value)}"${suffix}`
}

/**
 * Escapes a string for use in a regular expression.
 * @param {string} value Raw expression text.
 * @returns {string} Escaped expression text.
 */
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/**
 * Escapes an HTML attribute value.
 * @param {string} value Raw attribute value.
 * @returns {string} Escaped attribute value.
 */
function escapeAttribute(value) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
}

/**
 * Unescapes an HTML attribute value.
 * @param {string} value Escaped attribute value.
 * @returns {string} Raw attribute value.
 */
function unescapeAttribute(value) {
  return value
    .replace(/&quot;/g, "\"")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
}
